/*----------------------------------
- LEGAL ENTITY CATALOG (SSOT)
----------------------------------*/

/**
 * Trader identification for the public legal pages.
 *
 * Values come from the French company register (RCS Paris, SIREN 994 895 472).
 * French law goes further than the EU baseline: LCEN article 6-III also requires
 * a published website to name its publication director and its host, which is
 * why those two fields exist here.
 *
 * `isLegalEntityResolved` gates indexing, not rendering. While any field is
 * unresolved the three legal routes serve `noindex, nofollow` and stay out of
 * the sitemap, but they still render and stay linked, because the signup modal
 * binds users to Terms and Privacy and those links must resolve.
 */

/*----------------------------------
- TYPES
----------------------------------*/

export type LegalEntityDetails = {
    /** Registered company name (dénomination sociale). */
    name: string;
    /** Legal form, e.g. EURL, SAS. */
    legalForm: string;
    /** Share capital as displayed, currency included. */
    shareCapital: string;
    /** Registered address (siège social), single line. */
    address: string;
    /** Country of registration. Sets governing law and the supervisory authority. */
    country: string;
    /** Intra-community VAT number. */
    vatNumber: string;
    /** Registration in the local register, e.g. "RCS Paris 994 895 472". */
    registrationNumber: string;
    /** Public register record, so a reader can verify the company without leaving the page. */
    registerUrl: string;
    /** Mailbox that receives legal and privacy requests. */
    contactEmail: string;
    /** Directeur de la publication, required by LCEN article 6-III. */
    publicationDirector: string;
    /** Public profile of the publication director, for the same verification reason. */
    publicationDirectorUrl: string;
    /** Host name, required by LCEN article 6-III. */
    hostName: string;
    /** Host registered address, required by LCEN article 6-III. */
    hostAddress: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** Marks a value the business still has to supply. Greppable on purpose. */
export const LEGAL_ENTITY_UNRESOLVED = 'TO_BE_PROVIDED';

export const LEGAL_ENTITY: LegalEntityDetails = {
    name: 'FLUXEON',
    legalForm: 'EURL',
    shareCapital: '1 000 EUR',
    address: '61 rue de Lyon, 75012 Paris',
    country: 'France',
    vatNumber: 'FR17994895472',
    registrationNumber: 'RCS Paris 994 895 472',
    registerUrl: 'https://www.pappers.fr/entreprise/fluxeon-994895472',
    contactEmail: 'contact@unique.domains',
    /**
     * Both names on purpose. The founder is publicly Kai Etan everywhere else, but LCEN
     * identification only holds if a reader can match this value to the register and to
     * `publicationDirectorUrl`, which both carry the legal name.
     */
    publicationDirector: 'Kai Etan (Gaëtan Le Gac)',
    publicationDirectorUrl: 'https://www.linkedin.com/in/gaetanlegac/',
    hostName: 'Railway Corporation',
    hostAddress: '548 Market St PMB 68956, San Francisco, CA 94104, United States',
};

/**
 * Date the legal pages were last reviewed, rendered on each page. One date for the three pages: it moves with any
 * change to one of them, as the privacy page promises (2026-11-04: the attribution cookies, decision E22 2A).
 */
export const LEGAL_PAGES_LAST_UPDATED = '2026-11-04';

/*----------------------------------
- HELPERS
----------------------------------*/

export const isLegalEntityResolved = (entity: LegalEntityDetails = LEGAL_ENTITY): boolean =>
    Object.values(entity).every((value) => value.trim() !== '' && value !== LEGAL_ENTITY_UNRESOLVED);

/** Renders a value for display, so an unresolved field never reads as a real one. */
export const legalEntityValue = (value: string): string =>
    value === LEGAL_ENTITY_UNRESOLVED ? '[to be completed]' : value;

/**
 * "FLUXEON, EURL, share capital 1 000 EUR", the standard French identification line.
 * Split in two so the page can link the name to the register while the rest stays plain text.
 */
export const legalEntityIdentityParts = (
    entity: LegalEntityDetails = LEGAL_ENTITY,
): { name: string; details: string } => ({
    name: legalEntityValue(entity.name),
    details: `${legalEntityValue(entity.legalForm)}, share capital ${legalEntityValue(entity.shareCapital)}`,
});
